import React, { FunctionComponent, useEffect, useState } from 'react';
import { ICurrentUser } from '@jetslash/market-frontend-shared-core/src/types/models/user';

import useInterval from '@jetslash/market-frontend-shared-core/src/utilities/useInterval';
import { postResendEmailConfirmationInstructions } from '@jetslash/market-frontend-shared-core/src/api/userApi';
import { track } from '@/utility/eventTracking/eventTracker';
import { EVENT_SIGN_UP_STEP_VIEWED } from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/events';
import SignUpStepPropGroup from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/propertyGroupSerializers/SignUpStepPropGroup';
import DesignText from '@/components/typography/DesignText';
import useSignUpWizard from '@/hooks/useSignUpWizard';
import { parseErrorMessagesFromResponse } from '@jetslash/market-frontend-shared-core/src/utilities/form/formErrorsFromErrorResponse';
import { ValidationErrorMessages } from '@jetslash/market-frontend-shared-core/src/utilities/form/types';
import StartOverButton from './StartOverButton';
import FormErrorDisplay from '../forms/FormErrorDisplay';
import Reporty from '../../utility/errorHandling/Reporty';
import CTAButton from '../design/CTAButton';
import Col from '../layout/Col';
import Row from '../layout/Row';

interface IResendButtonProps {
  loading?: boolean;
  signUpUser: ICurrentUser;
}
const TimedResendEmailConfirmationButton: FunctionComponent<IResendButtonProps> = ({ signUpUser, loading }) => {
  const secondsToDisableBetweenRequests = 60;
  const [secondsUntilActive, setSecondsUntilActive] = useState(secondsToDisableBetweenRequests);
  const { signUpStepStartRequest, signUpStepSuccess, signUpStepFailure } = useSignUpWizard();

  useInterval(() => {
    setSecondsUntilActive(secondsUntilActive - 1);
  }, 1000);

  let buttonDisabled = true;
  let buttonText = `Resend in ${secondsUntilActive} seconds`;

  if (secondsUntilActive < 1) {
    buttonDisabled = false;
    buttonText = 'Resend confirmation email';
  }

  const onClick = async () => {
    signUpStepStartRequest();
    try {
      setSecondsUntilActive(secondsToDisableBetweenRequests);
      await postResendEmailConfirmationInstructions(signUpUser.email);
      signUpStepSuccess();
    } catch (err) {
      const errors = parseErrorMessagesFromResponse(err);
      signUpStepFailure(errors);
      Reporty.error(err);
    }
  };

  if (buttonDisabled) {
    return <div style={{ height: 46, lineHeight: '46px' }}>{buttonText}</div>;
  }

  return (
    <CTAButton onClick={onClick} icon="redo" type="default" text={buttonText} disabled={buttonDisabled || loading} />
  );
};

interface IProps {
  signUpUser: ICurrentUser;
  loading?: boolean;
  errors?: ValidationErrorMessages;
}

const SignUpEmailVerificationInstructions: FunctionComponent<IProps> = ({ signUpUser, loading, errors }) => {
  const { updateSignUpUser } = useSignUpWizard();

  useEffect(() => {
    track(
      EVENT_SIGN_UP_STEP_VIEWED,
      SignUpStepPropGroup.create(signUpUser, SignUpStepPropGroup.signUpSteps.EMAIL_VERIFICATION),
    );
  }, []);

  useEffect(() => {
    updateSignUpUser();
  }, []);

  useInterval(() => {
    updateSignUpUser();
  }, 5000);

  return (
    <div style={{ marginLeft: 10, marginRight: 10 }}>
      <Row>
        <Col span={24} textLeft style={{ marginBottom: 10 }}>
          We just sent a confirmation email to <DesignText strong>{signUpUser.email}</DesignText>. Click the link in the
          email to proceed.
        </Col>
        <FormErrorDisplay errors={errors} />
        <Col span={24} textCenter style={{ marginTop: 20 }}>
          <TimedResendEmailConfirmationButton signUpUser={signUpUser} loading={loading} />
        </Col>
        <Col span={24} textCenter style={{ paddingTop: 10 }}>
          <StartOverButton />
        </Col>
      </Row>
    </div>
  );
};

export default SignUpEmailVerificationInstructions;
